import { useLocalization } from '@magicdoorinc/ui';
import { createMemo } from 'solid-js';
import AccountingIcon from '~/assets/images/sidebar/accounting.svg';
import IconAiStar from '~/assets/images/sidebar/ai.svg';
import IconAssociation from '~/assets/images/sidebar/association.svg';
import CommunicationsIcon from '~/assets/images/sidebar/communications.svg';
import IconHome from '~/assets/images/sidebar/home.svg';
import IconListing from '~/assets/images/sidebar/listing.svg';
import IconMaintenance from '~/assets/images/sidebar/maintenance.svg';
import IconOnboarding from '~/assets/images/sidebar/onboarding.svg';
import IconLeasing from '~/assets/images/sidebar/paper.svg';
import IconProperty from '~/assets/images/sidebar/property.svg';
import IconReports from '~/assets/images/sidebar/reports.svg';
import IconSettings from '~/assets/images/sidebar/settings.svg';
import IconUsers from '~/assets/images/sidebar/users.svg';
import { filterNavigationItems, NAVIGATION_CONFIG } from '~/config/navigation';
import { useCurrentCompany, useFeatureHighlights, usePropertyManagerPermissions } from '~/contexts/global';
import { useDeviceDetect } from '~/hooks/usedeviceDetect';
import { getHoaInjectedChildren, IS_HOA_DEMO_MODE, isHoaMenuItemHidden, isHoaOnlyMenuItem } from '~/utils/hoaDemoMode';
import type { MenuItem } from '~/components/layouts/Menu/types/menuItem';
import type { NavigationItem } from '~/config/navigation';

const ICON_MAP: Record<string, { icon: string; selectedIcon: string }> = {
  Onboarding: { icon: IconOnboarding, selectedIcon: IconOnboarding },
  Home: { icon: IconHome, selectedIcon: IconHome },
  Properties: { icon: IconProperty, selectedIcon: IconProperty },
  Listing: { icon: IconListing, selectedIcon: IconListing },
  Leasing: { icon: IconLeasing, selectedIcon: IconLeasing },
  Users: { icon: IconUsers, selectedIcon: IconUsers },
  Accounting: { icon: AccountingIcon, selectedIcon: AccountingIcon },
  Reports: { icon: IconReports, selectedIcon: IconReports },
  Maintenance: { icon: IconMaintenance, selectedIcon: IconMaintenance },
  Associations: { icon: IconAssociation, selectedIcon: IconAssociation },
  Communications: { icon: CommunicationsIcon, selectedIcon: CommunicationsIcon },
  AI: { icon: IconAiStar, selectedIcon: IconAiStar },
  Settings: { icon: IconSettings, selectedIcon: IconSettings },
};

const normalizePath = (path: string): string => {
  const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;
  return withLeadingSlash.length > 1 && withLeadingSlash.endsWith('/') ? withLeadingSlash.slice(0, -1) : withLeadingSlash;
};

const collectHighlightedIds = (highlightPaths: string[]): Set<string> => {
  const flagged = new Set<string>();

  for (const rawPath of highlightPaths) {
    const path = normalizePath(rawPath);
    let best: { id: string; parentId?: string; length: number } | undefined;

    const walk = (items: NavigationItem[], parentId?: string) => {
      for (const item of items) {
        const itemPath = normalizePath(item.path);
        const matches = itemPath === '/' ? path === '/' : path === itemPath || path.startsWith(`${itemPath}/`);
        if (matches && (!best || itemPath.length > best.length)) {
          best = { id: item.id, parentId, length: itemPath.length };
        }
        if (item.children) walk(item.children, item.id);
      }
    };

    walk(NAVIGATION_CONFIG);

    if (best) {
      flagged.add(best.id);
      if (best.parentId) flagged.add(best.parentId);
    }
  }

  return flagged;
};

function navigationItemToMenuItem(
  item: NavigationItem,
  t: (key: string) => string,
  isMobileView: boolean,
  highlightedIds: Set<string>
): MenuItem {
  const icons = ICON_MAP[item.id];
  const visibleChildren = item.children?.filter((child) => {
    if (isHoaOnlyMenuItem(child.id)) return IS_HOA_DEMO_MODE;
    return !isHoaMenuItemHidden(child.id);
  });

  return {
    id: item.id,
    text: t(item.id),
    path: item.path,
    canNavigate: item.canNavigate,
    ...(icons && { icon: icons.icon, selectedIcon: icons.selectedIcon }),
    ...(item.realPath && { realPath: item.realPath }),
    // Special handling for Settings mobile redirect
    ...(item.id === 'Settings' && !isMobileView && { replace: '/settings' }),
    ...(highlightedIds.has(item.id) && { hasHighlight: true }),
    ...(visibleChildren && {
      subMenuItems: visibleChildren.map((child) => navigationItemToMenuItem(child, t, isMobileView, highlightedIds)),
    }),
  };
}

export const useMenuItems = () => {
  const { t } = useLocalization();
  const { isMobileView } = useDeviceDetect();
  const { menuAccess } = usePropertyManagerPermissions();
  const { checkFeature } = useCurrentCompany();
  const { highlights } = useFeatureHighlights();

  // 递归过滤菜单树，移除没有权限的菜单项和子菜单项
  const filterMenuTree = (menuItem: MenuItem): MenuItem | null => {
    const key = menuItem.id.toLowerCase();

    // 如果主路径在 menuAccess 列表中，则保留该菜单项（并且保留其所有子菜单，因为不再根据子权限过滤）
    if (menuAccess().includes(key)) {
      return menuItem;
    }

    // 否则返回 null，表示该菜单项应该被过滤掉
    return null;
  };

  const menus = createMemo(() => {
    const filteredNavItems = filterNavigationItems(NAVIGATION_CONFIG, checkFeature);
    const highlightedIds = collectHighlightedIds((highlights()?.items ?? []).map((item) => item.path));

    const allMenus = filteredNavItems
      .filter((navItem) => {
        if (isHoaOnlyMenuItem(navItem.id)) return IS_HOA_DEMO_MODE;
        return !isHoaMenuItemHidden(navItem.id);
      })
      .map((navItem) => navigationItemToMenuItem(navItem, t, isMobileView(), highlightedIds))
      .filter((menuItem) => {
        // Special filtering for Onboarding menu
        if (menuItem.id === 'Onboarding') {
          return false;
        }
        return true;
      });

    // Apply permission filtering - recursively filter entire menu tree
    const filtered = allMenus.map((menuItem) => filterMenuTree(menuItem)).filter((item): item is MenuItem => item !== null);

    if (!IS_HOA_DEMO_MODE) return filtered;
    // (HOA mode below injects the extra association children on top of the filtered tree)

    const filteredIds = new Set(filtered.map((item) => item.id));
    const result = filtered.map((menuItem) => {
      const injected = getHoaInjectedChildren(menuItem.id);
      if (injected.length === 0) return menuItem;
      const injectedItems: MenuItem[] = injected.map((child) => ({
        id: child.id,
        text: t(child.id),
        path: child.path,
        canNavigate: true,
      }));
      return {
        ...menuItem,
        subMenuItems: [...(menuItem.subMenuItems ?? []), ...injectedItems],
      };
    });

    // Ensure HOA-injected parents (e.g. Associations) appear even when the user
    // has no permission to the underlying real children.
    allMenus.forEach((menuItem) => {
      if (filteredIds.has(menuItem.id)) return;
      const injected = getHoaInjectedChildren(menuItem.id);
      if (injected.length === 0) return;
      result.push({
        ...menuItem,
        subMenuItems: injected.map((child) => ({
          id: child.id,
          text: t(child.id),
          path: child.path,
          canNavigate: true,
        })),
      });
    });

    return result;
  });

  return menus;
};
